import * as THREE from 'three';
import { cloth, leather, std } from '../materials.ts';
import { G, M, ring } from '../prims.ts';
import { figure } from './heroes.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * The ranger's path (docs/sendas/explorador.md): its people, painted miniatures.
 *
 * - Sela: a walker of sixteen, thin and quick, a short grey-green walker's cape with a red edge, a slate jerkin, short
 *   dark hair cut with a knife, a coil of rope over each shoulder (the two she counts), the rope's knife at her belt,
 *   a walker's staff with a red rag tied at its top.
 *
 * - Galo and his crew (chapter 2): see galo() and crew().
 *
 * Colors: cold greys and greens of the rim, the dragon's red only in the rag and the cape's edge; no gold.
 */

const ROPE = '#6d746c';

/** Sela: sixteen, two coils of rope over her shoulders, the knife at her belt, a staff with a red rag. */
const sela = (o?: BaseOpts): Model => {
  const c = begin(o, 0.58, 1801);
  const skin = std('#d6ae95', { roughness: 0.85 });
  const jerkin = leather('#3e4650');
  const wool = cloth('#5b6a62');
  const f = figure({ legs: cloth('#2e3236'), boots: leather('#221d19'), torso: jerkin, shoulders: wool, arms: wool, forearms: leather('#363b42'), hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(0.88, 0.92, 0.88);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The short cape: grey-green wool, its edge red.
  const cape = cloth('#55665d');
  const cp = G(b, 0, 1.45, -0.02);
  M(new THREE.CylinderGeometry(0.2, 0.34, 0.62, 12, 1, true, Math.PI * 0.6, Math.PI * 0.8), cape, cp).position.y = -0.31;
  M(new THREE.CylinderGeometry(0.342, 0.345, 0.05, 12, 1, true, Math.PI * 0.6, Math.PI * 0.8), cloth('#a8362c'), cp).position.y = -0.6;
  // Two coils of rope, one over each shoulder, slung across the chest.
  const rope = std(ROPE, { roughness: 1 });
  for (const k of [-1, 1]) {
    const coil = M(new THREE.TorusGeometry(0.17, 0.035, 6, 16), rope, b);
    coil.position.set(k * 0.06, 1.22, 0.02);
    coil.rotation.set(0.15, 0, k * 0.75);
    coil.scale.set(1, 1.25, 1);
    const coil2 = M(new THREE.TorusGeometry(0.15, 0.03, 6, 14), rope, b);
    coil2.position.set(k * 0.07, 1.22, 0.03);
    coil2.rotation.set(0.15, 0, k * 0.75);
    coil2.scale.set(1, 1.25, 1);
  }
  // The rope's knife at her belt.
  M(new THREE.BoxGeometry(0.03, 0.15, 0.02), leather('#2a221d'), b).position.set(0.17, 0.9, 0.12);
  M(new THREE.BoxGeometry(0.02, 0.05, 0.02), std('#9aa1a8', { metalness: 0.5, roughness: 0.4 }), b).position.set(0.17, 1.0, 0.12);
  // Short dark hair, cut ragged.
  const hair = std('#231c19', { roughness: 1 });
  const top = M(new THREE.SphereGeometry(0.136, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.6), hair, h);
  top.position.set(0, 1.648, -0.01);
  top.scale.set(1.06, 1, 1.1);
  for (let i = 0; i < 5; i++) {
    const t = M(new THREE.ConeGeometry(0.03, 0.07, 4), hair, h);
    t.position.set(-0.1 + i * 0.05, 1.6, 0.1);
    t.rotation.x = Math.PI + 0.3;
  }
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.042, 1.652, 0.116);
  const nose = M(new THREE.ConeGeometry(0.016, 0.042, 5), std('#cfa38a', { roughness: 0.85 }), h);
  nose.position.set(0, 1.624, 0.13);
  nose.rotation.x = Math.PI / 2 + 0.2;
  // The walker's staff, a red rag tied at its top.
  const st = G(f.rhAbs, 0.26, 0.95, 0.12);
  M(new THREE.CylinderGeometry(0.018, 0.022, 1.55, 6), leather('#3a3430'), st).position.y = 0.2;
  const rag = M(new THREE.BoxGeometry(0.1, 0.16, 0.01), cloth('#c23a2e'), st);
  rag.position.set(0.05, 0.9, 0);
  rag.rotation.z = 0.25;
  ring(st, 0.024, 0.008, rope, 0.86, 3, 8);
  f.rig.armR.rotation.x = -0.25;
  return done(c, 1.62, () => undefined, f.rig);
};

/**
 * Galo: past seventy, tall and spare, a long red walker's cape (the flank's most famous: the colour reads from the far
 * wall), a grey beard cut short, a weathered slate coat, a heavy coil of red rope over one shoulder, his knife at the
 * belt (the one: it shows), a staff with no rag.
 */
const galo = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 1802);
  const skin = std('#c99a80', { roughness: 0.9 });
  const coat = leather('#3a4048');
  const wool = cloth('#474e55');
  const f = figure({ legs: cloth('#2a2d31'), boots: leather('#1f1a17'), torso: coat, shoulders: wool, arms: wool, forearms: leather('#30353b'), hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(0.95, 1.04, 0.95);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The long red cape, to the calves, a hood thrown back.
  const red = cloth('#b8322a');
  const cp = G(b, 0, 1.46, -0.02);
  M(new THREE.CylinderGeometry(0.21, 0.4, 1.0, 14, 1, true, Math.PI * 0.55, Math.PI * 0.9), red, cp).position.y = -0.5;
  M(new THREE.TorusGeometry(0.14, 0.05, 6, 12, Math.PI), red, cp).position.set(0, 0.04, -0.06);
  // The red rope, a heavy coil over the left shoulder.
  const rope = std('#a8332a', { roughness: 0.95, emissive: '#2a0806' });
  const coil = M(new THREE.TorusGeometry(0.19, 0.045, 6, 16), rope, b);
  coil.position.set(-0.08, 1.2, 0.03);
  coil.rotation.set(0.15, 0, -0.8);
  coil.scale.set(1, 1.3, 1);
  // The knife at the belt: long, its haft worn pale.
  M(new THREE.BoxGeometry(0.035, 0.22, 0.02), leather('#2a221d'), b).position.set(0.18, 0.86, 0.13);
  M(new THREE.BoxGeometry(0.025, 0.07, 0.025), std('#cfc6b6', { roughness: 0.6 }), b).position.set(0.18, 1.0, 0.13);
  // Grey hair thinning, a short grey beard.
  const grey = std('#b9bcbd', { roughness: 1 });
  const top = M(new THREE.SphereGeometry(0.134, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.42), grey, h);
  top.position.set(0, 1.652, -0.02);
  const beard = M(new THREE.SphereGeometry(0.1, 10, 8, 0, Math.PI * 2, Math.PI * 0.45, Math.PI * 0.55), grey, h);
  beard.position.set(0, 1.6, 0.05);
  beard.scale.set(1.05, 1.1, 0.9);
  for (const k of [-1, 1]) M(new THREE.SphereGeometry(0.011, 5, 4), std('#141214'), h).position.set(k * 0.042, 1.655, 0.118);
  // The staff, bare.
  const st = G(f.rhAbs, 0.26, 0.95, 0.12);
  M(new THREE.CylinderGeometry(0.02, 0.024, 1.7, 6), leather('#2e2925'), st).position.y = 0.26;
  f.rig.armR.rotation.x = -0.2;
  return done(c, 1.74, () => undefined, f.rig);
};

/** One of Galo's crew: young, a short red-grey cape, a coil of red rope, a lantern or a hammer at the belt. */
const crew = (o: BaseOpts | undefined, k: number): Model => {
  const c = begin(o, 0.57, 1803 + k);
  const skin = std(['#d4ab90', '#b58569', '#e0bca4'][k % 3] as string, { roughness: 0.85 });
  const jerkin = leather(['#3b424a', '#454038', '#353c44'][k % 3] as string);
  const wool = cloth(['#5a5e63', '#4f5550', '#61575a'][k % 3] as string);
  const f = figure({ legs: cloth('#2c3034'), boots: leather('#221d19'), torso: jerkin, shoulders: wool, arms: wool, forearms: leather('#363b42'), hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(0.9, 0.94 + (k % 2) * 0.04, 0.9);
  const b = f.bodyAbs;
  const h = f.headAbs;
  const cp = G(b, 0, 1.45, -0.02);
  M(new THREE.CylinderGeometry(0.2, 0.33, 0.6, 12, 1, true, Math.PI * 0.6, Math.PI * 0.8), cloth('#8a2c24'), cp).position.y = -0.3;
  const rope = std('#a8332a', { roughness: 0.95 });
  const coil = M(new THREE.TorusGeometry(0.16, 0.035, 6, 16), rope, b);
  coil.position.set(k % 2 ? 0.07 : -0.07, 1.22, 0.02);
  coil.rotation.set(0.15, 0, k % 2 ? 0.75 : -0.75);
  coil.scale.set(1, 1.25, 1);
  const hair = std(['#3a2a22', '#151214', '#6b4a30'][k % 3] as string, { roughness: 1 });
  const top = M(new THREE.SphereGeometry(0.138, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), hair, h);
  top.position.set(0, 1.648, -0.01);
  for (const s2 of [-1, 1]) M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(s2 * 0.042, 1.652, 0.116);
  if (k === 0) {
    // A lantern at the belt.
    M(new THREE.BoxGeometry(0.08, 0.11, 0.08), std('#30353a', { metalness: 0.5 }), b).position.set(-0.18, 0.86, 0.1);
    M(new THREE.BoxGeometry(0.05, 0.07, 0.05), std('#ffd2a0', { emissive: '#ffb070' }), b).position.set(-0.18, 0.86, 0.1);
  } else M(new THREE.BoxGeometry(0.04, 0.18, 0.04), leather('#3a3430'), b).position.set(0.18, 0.88, 0.12);
  return done(c, 1.6, () => undefined, f.rig);
};

export const asterModel = (id: string, o?: BaseOpts): Model | null => {
  switch (id) {
    case 'sela':
      return sela(o);
    case 'galo':
      return galo(o);
    case 'galoCrew1':
      return crew(o, 0);
    case 'galoCrew2':
      return crew(o, 1);
    case 'galoCrew3':
      return crew(o, 2);
    default:
      return null;
  }
};
